import { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Spinner,
  Button,
  Input,
  Switch,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Divider,
  useColorModeValue,
  Select,
} from '@chakra-ui/react'
import { FloppyDisk } from '@phosphor-icons/react'
import api from '../services/api'

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']

interface BusinessHour {
  id: number
  dayOfWeek: number
  startTime: string
  endTime: string
  isActive: boolean
}

type TimeUnit = 'h' | 'm' | 's'

interface CriticalThresholds {
  id: number
  maxSecondsWithoutResolution: number
  maxSecondsWithoutStart: number
  maxUserMessagesWithoutResponse: number
}

function toSeconds(value: number, unit: TimeUnit): number {
  if (unit === 'h') return Math.max(1, Math.round(value * 3600))
  if (unit === 'm') return Math.max(1, Math.round(value * 60))
  return Math.max(1, Math.round(value))
}

function fromSecondsForUnit(totalSeconds: number, unit: TimeUnit): number {
  if (unit === 'h') return Math.max(1, Math.round(totalSeconds / 3600))
  if (unit === 'm') return Math.max(1, Math.round(totalSeconds / 60))
  return Math.max(1, Math.round(totalSeconds))
}

function defaultUnitForSeconds(sec: number): TimeUnit {
  if (sec < 60) return 's'
  if (sec < 3600) return 'm'
  return 'h'
}

function maxInputForUnit(unit: TimeUnit, capSeconds: number): number {
  if (unit === 'h') return Math.max(1, Math.floor(capSeconds / 3600))
  if (unit === 'm') return Math.max(1, Math.floor(capSeconds / 60))
  return capSeconds
}

const MAX_RESOLUTION_SEC = 90 * 24 * 3600
const MAX_START_SEC = 30 * 24 * 3600

function formatTimeForInput(time: string): string {
  if (!time) return '08:00'
  const parts = time.split(':')
  return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`
}

export default function Settings() {
  const toast = useToast()
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const [isLoadingBH, setIsLoadingBH] = useState(true)
  const [isLoadingCT, setIsLoadingCT] = useState(true)
  const [isSavingBH, setIsSavingBH] = useState(false)
  const [isSavingCT, setIsSavingCT] = useState(false)
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([])
  const [thresholds, setThresholds] = useState<CriticalThresholds>({
    id: 0,
    maxSecondsWithoutResolution: 24 * 3600,
    maxSecondsWithoutStart: 4 * 3600,
    maxUserMessagesWithoutResponse: 5,
  })
  const [resolutionUnit, setResolutionUnit] = useState<TimeUnit>('h')
  const [startUnit, setStartUnit] = useState<TimeUnit>('h')

  useEffect(() => {
    fetchBusinessHours()
    fetchThresholds()
  }, [])

  const fetchBusinessHours = async () => {
    setIsLoadingBH(true)
    try {
      const res = await api.get('/admin/settings/business-hours')
      setBusinessHours(res.data)
    } catch (error) {
      console.error('Error fetching business hours:', error)
    } finally {
      setIsLoadingBH(false)
    }
  }

  const fetchThresholds = async () => {
    setIsLoadingCT(true)
    try {
      const res = await api.get('/admin/settings/critical-thresholds')
      const d = res.data
      const resSec = d.maxSecondsWithoutResolution ?? (d.maxHoursWithoutResolution ?? 24) * 3600
      const startSec = d.maxSecondsWithoutStart ?? (d.maxHoursWithoutStart ?? 4) * 3600
      setThresholds({
        id: d.id ?? 0,
        maxSecondsWithoutResolution: resSec,
        maxSecondsWithoutStart: startSec,
        maxUserMessagesWithoutResponse: d.maxUserMessagesWithoutResponse ?? 5,
      })
      setResolutionUnit(defaultUnitForSeconds(resSec))
      setStartUnit(defaultUnitForSeconds(startSec))
    } catch (error) {
      console.error('Error fetching thresholds:', error)
    } finally {
      setIsLoadingCT(false)
    }
  }

  const saveBusinessHours = async () => {
    setIsSavingBH(true)
    try {
      const hours = businessHours.map((bh) => ({
        dayOfWeek: bh.dayOfWeek,
        startTime: bh.startTime,
        endTime: bh.endTime,
        isActive: bh.isActive,
      }))
      await api.put('/admin/settings/business-hours', { hours })
      toast({ title: 'Horários salvos', status: 'success', duration: 3000 })
    } catch (error) {
      console.error('Error saving business hours:', error)
      toast({ title: 'Erro ao salvar horários', status: 'error', duration: 3000 })
    } finally {
      setIsSavingBH(false)
    }
  }

  const saveThresholds = async () => {
    setIsSavingCT(true)
    try {
      await api.put('/admin/settings/critical-thresholds', {
        maxSecondsWithoutResolution: thresholds.maxSecondsWithoutResolution,
        maxSecondsWithoutStart: thresholds.maxSecondsWithoutStart,
        maxUserMessagesWithoutResponse: thresholds.maxUserMessagesWithoutResponse,
      })
      toast({ title: 'Limiares salvos', status: 'success', duration: 3000 })
    } catch (error) {
      console.error('Error saving thresholds:', error)
      toast({ title: 'Erro ao salvar limiares', status: 'error', duration: 3000 })
    } finally {
      setIsSavingCT(false)
    }
  }

  const updateBH = (dayOfWeek: number, field: keyof BusinessHour, value: any) => {
    setBusinessHours((prev) =>
      prev.map((bh) => (bh.dayOfWeek === dayOfWeek ? { ...bh, [field]: value } : bh))
    )
  }

  if (isLoadingBH && isLoadingCT) {
    return (
      <Box p={6} h="100%" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="yellow.500" />
      </Box>
    )
  }

  return (
    <Box p={6} h="100%" overflowY="auto">
      <VStack align="stretch" spacing={8}>
        <Text fontSize="2xl" fontWeight="bold" color={textPrimary}>
          Configurações
        </Text>

        {/* Business Hours */}
        <Box bg={surfaceBg} borderRadius="xl" p={6}>
          <VStack align="stretch" spacing={5}>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontWeight="semibold" color={textPrimary} fontSize="lg">
                  Horário de Funcionamento
                </Text>
                <Text fontSize="sm" color={textMuted}>
                  Define quando o atendimento está ativo para cálculo de métricas em horário útil
                </Text>
              </VStack>
              <Button
                colorScheme="yellow"
                size="sm"
                leftIcon={<FloppyDisk size={16} />}
                onClick={saveBusinessHours}
                isLoading={isSavingBH}
              >
                Salvar
              </Button>
            </HStack>

            {isLoadingBH ? (
              <Spinner size="md" color="yellow.500" />
            ) : (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                  <Th color={textMuted}>Dia</Th>
                  <Th color={textMuted}>Ativo</Th>
                  <Th color={textMuted}>Início</Th>
                  <Th color={textMuted}>Fim</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {businessHours.map((bh) => (
                      <Tr key={bh.dayOfWeek} opacity={bh.isActive ? 1 : 0.5}>
                        <Td>
                          <HStack>
                            <Text color={textPrimary} fontSize="sm" fontWeight="medium">
                              {DAY_NAMES[bh.dayOfWeek]}
                            </Text>
                            {!bh.isActive && (
                              <Badge colorScheme="gray" fontSize="xs">Inativo</Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td>
                          <Switch
                            colorScheme="yellow"
                            isChecked={bh.isActive}
                            onChange={(e) => updateBH(bh.dayOfWeek, 'isActive', e.target.checked)}
                          />
                        </Td>
                        <Td>
                          <Input
                            type="time"
                            size="sm"
                            w="130px"
                            bg={subtleBg}
                            borderColor={borderSubtle}
                            value={formatTimeForInput(bh.startTime)}
                            onChange={(e) => updateBH(bh.dayOfWeek, 'startTime', e.target.value + ':00')}
                            isDisabled={!bh.isActive}
                          />
                        </Td>
                        <Td>
                          <Input
                            type="time"
                            size="sm"
                            w="130px"
                            bg={subtleBg}
                            borderColor={borderSubtle}
                            value={formatTimeForInput(bh.endTime)}
                            onChange={(e) => updateBH(bh.dayOfWeek, 'endTime', e.target.value + ':00')}
                            isDisabled={!bh.isActive}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </Box>

        <Divider borderColor={borderColor} />

        {/* Critical Thresholds */}
        <Box bg={surfaceBg} borderRadius="xl" p={6}>
          <VStack align="stretch" spacing={5}>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text fontWeight="semibold" color={textPrimary} fontSize="lg">
                  Limiares de Chamados Críticos
                </Text>
                <Text fontSize="sm" color={textMuted}>
                  Define os limites para identificar chamados críticos na aba de métricas
                </Text>
              </VStack>
              <Button
                colorScheme="yellow"
                size="sm"
                leftIcon={<FloppyDisk size={16} />}
                onClick={saveThresholds}
                isLoading={isSavingCT}
              >
                Salvar
              </Button>
            </HStack>

            {isLoadingCT ? (
              <Spinner size="md" color="yellow.500" />
            ) : (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Box>
                  <Text color={textSecondary} fontSize="sm" mb={2}>
                    Tempo máx. sem resolução
                  </Text>
                  <HStack align="flex-start" spacing={2}>
                    <NumberInput
                      value={fromSecondsForUnit(thresholds.maxSecondsWithoutResolution, resolutionUnit)}
                      onChange={(_, val) => {
                        const n = val ?? 1
                        const sec = Math.min(MAX_RESOLUTION_SEC, Math.max(1, toSeconds(n, resolutionUnit)))
                        setThresholds((prev) => ({ ...prev, maxSecondsWithoutResolution: sec }))
                      }}
                      min={1}
                      max={maxInputForUnit(resolutionUnit, MAX_RESOLUTION_SEC)}
                      size="sm"
                      flex={1}
                    >
                      <NumberInputField bg={subtleBg} borderColor={borderSubtle} />
                      <NumberInputStepper>
                        <NumberIncrementStepper borderColor={borderSubtle} />
                        <NumberDecrementStepper borderColor={borderSubtle} />
                      </NumberInputStepper>
                    </NumberInput>
                    <Select
                      size="sm"
                      maxW="118px"
                      value={resolutionUnit}
                      onChange={(e) => setResolutionUnit(e.target.value as TimeUnit)}
                      bg={subtleBg}
                      borderColor={borderSubtle}
                    >
                      <option value="h">Horas</option>
                      <option value="m">Minutos</option>
                      <option value="s">Segundos</option>
                    </Select>
                  </HStack>
                  <Text color={textMuted} fontSize="xs" mt={1}>
                    Chamados abertos além desse tempo (sem concluir) entram como críticos
                  </Text>
                </Box>

                <Box>
                  <Text color={textSecondary} fontSize="sm" mb={2}>
                    Tempo máx. sem início (pendente)
                  </Text>
                  <HStack align="flex-start" spacing={2}>
                    <NumberInput
                      value={fromSecondsForUnit(thresholds.maxSecondsWithoutStart, startUnit)}
                      onChange={(_, val) => {
                        const n = val ?? 1
                        const sec = Math.min(MAX_START_SEC, Math.max(1, toSeconds(n, startUnit)))
                        setThresholds((prev) => ({ ...prev, maxSecondsWithoutStart: sec }))
                      }}
                      min={1}
                      max={maxInputForUnit(startUnit, MAX_START_SEC)}
                      size="sm"
                      flex={1}
                    >
                      <NumberInputField bg={subtleBg} borderColor={borderSubtle} />
                      <NumberInputStepper>
                        <NumberIncrementStepper borderColor={borderSubtle} />
                        <NumberDecrementStepper borderColor={borderSubtle} />
                      </NumberInputStepper>
                    </NumberInput>
                    <Select
                      size="sm"
                      maxW="118px"
                      value={startUnit}
                      onChange={(e) => setStartUnit(e.target.value as TimeUnit)}
                      bg={subtleBg}
                      borderColor={borderSubtle}
                    >
                      <option value="h">Horas</option>
                      <option value="m">Minutos</option>
                      <option value="s">Segundos</option>
                    </Select>
                  </HStack>
                  <Text color={textMuted} fontSize="xs" mt={1}>
                    Pendentes sem atendente além desse tempo entram como críticos
                  </Text>
                </Box>

                <Box>
                  <Text color={textSecondary} fontSize="sm" mb={2}>
                    Max. mensagens sem resposta
                  </Text>
                  <NumberInput
                    value={thresholds.maxUserMessagesWithoutResponse}
                    onChange={(_, val) =>
                      setThresholds((prev) => ({ ...prev, maxUserMessagesWithoutResponse: val || 0 }))
                    }
                    min={1}
                    max={50}
                    size="sm"
                  >
                    <NumberInputField bg={subtleBg} borderColor={borderSubtle} />
                    <NumberInputStepper>
                      <NumberIncrementStepper borderColor={borderSubtle} />
                      <NumberDecrementStepper borderColor={borderSubtle} />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text color={textMuted} fontSize="xs" mt={1}>
                    Mais de {thresholds.maxUserMessagesWithoutResponse} msgs do usuário sem resposta do atendente
                  </Text>
                </Box>
              </SimpleGrid>
            )}
          </VStack>
        </Box>
      </VStack>
    </Box>
  )
}
